"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { getRootDomain } from "@/lib/cookies/root-domain"

export type Theme = "light" | "dark" | "system"
export type ResolvedTheme = "light" | "dark"

const COOKIE_NAME = "gc-theme"
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60
const DARK_QUERY = "(prefers-color-scheme: dark)"

interface ThemeContextValue {
  theme: Theme | undefined
  resolvedTheme: ResolvedTheme | undefined
  setTheme: (next: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

function readCookie(): Theme | undefined {
  if (typeof document === "undefined") return undefined
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${COOKIE_NAME}=`))
  if (!match) return undefined
  const value = match.substring(match.indexOf("=") + 1)
  if (value === "light" || value === "dark" || value === "system") return value
  return undefined
}

function writeCookie(value: Theme): void {
  if (typeof document === "undefined") return
  let cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`
  const rootDomain = getRootDomain()
  if (rootDomain) cookie += `; domain=${rootDomain}`
  document.cookie = cookie
}

function getSystemPreference(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light"
  return window.matchMedia(DARK_QUERY).matches ? "dark" : "light"
}

function resolve(theme: Theme | undefined, system: ResolvedTheme): ResolvedTheme | undefined {
  if (theme === undefined) return undefined
  if (theme === "system") return system
  return theme
}

function applyClass(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return
  const root = document.documentElement
  if (resolved === "dark") root.classList.add("dark")
  else root.classList.remove("dark")
}

/**
 * Briefly suppress CSS transitions while we flip the dark class so colors snap
 * instead of animating. Mirrors next-themes' `disableTransitionOnChange`.
 */
function suppressTransitions(): () => void {
  if (typeof document === "undefined") return () => {}
  const style = document.createElement("style")
  style.appendChild(
    document.createTextNode(
      "*{-webkit-transition:none!important;-moz-transition:none!important;-o-transition:none!important;-ms-transition:none!important;transition:none!important}"
    )
  )
  document.head.appendChild(style)
  return () => {
    // Force reflow so the transition-disable style is applied before we remove it.
    window.getComputedStyle(document.body)
    setTimeout(() => {
      if (style.parentNode) style.parentNode.removeChild(style)
    }, 1)
  }
}

export interface ThemeProviderProps {
  children: ReactNode
  /** Theme to use when no cookie is set. Defaults to "light". */
  defaultTheme?: Theme
  /** Suppress CSS transitions during theme changes. Defaults to true. */
  disableTransitionOnChange?: boolean
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
  disableTransitionOnChange = true,
}: ThemeProviderProps) {
  // Theme is undefined during SSR + first client render to match the inline
  // <head> script's pre-hydration class application without causing a hydration
  // mismatch on theme-aware content.
  const [theme, setThemeState] = useState<Theme | undefined>(undefined)
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>("light")

  // Hydrate from cookie on mount.
  useEffect(() => {
    const cookieTheme = readCookie()
    const initial = cookieTheme ?? defaultTheme
    setSystemTheme(getSystemPreference())
    setThemeState(initial)
    // Don't write the cookie here — the inline <head> script already applied
    // the correct class. Writing on mount would re-introduce the clobber bug.
  }, [defaultTheme])

  // Track OS theme changes (only meaningful when theme === "system").
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const mql = window.matchMedia(DARK_QUERY)
    const onChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light")
    }
    if (mql.addEventListener) mql.addEventListener("change", onChange)
    else mql.addListener(onChange)
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange)
      else mql.removeListener(onChange)
    }
  }, [])

  // Whenever resolved theme changes, sync the <html class>.
  // The inline <head> script handles first paint; this handles every change after.
  const lastApplied = useRef<ResolvedTheme | undefined>(undefined)
  useEffect(() => {
    const resolved = resolve(theme, systemTheme)
    if (resolved === undefined) return
    if (lastApplied.current === resolved) return
    lastApplied.current = resolved
    applyClass(resolved)
  }, [theme, systemTheme])

  const setTheme = useCallback(
    (next: Theme) => {
      if (next !== "light" && next !== "dark" && next !== "system") return
      const restoreTransitions = disableTransitionOnChange ? suppressTransitions() : null
      setThemeState(next)
      writeCookie(next)
      // Apply class synchronously so the user sees the change immediately
      // without waiting for the next render.
      const resolved = resolve(next, getSystemPreference())
      if (resolved !== undefined) {
        lastApplied.current = resolved
        applyClass(resolved)
      }
      if (restoreTransitions) restoreTransitions()
    },
    [disableTransitionOnChange]
  )

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolvedTheme: resolve(theme, systemTheme),
      setTheme,
    }),
    [theme, systemTheme, setTheme]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (ctx === undefined) {
    // Match next-themes' behavior: outside a provider, return a no-op shape
    // rather than throwing. Some pages (e.g. error boundaries) may render
    // without the provider tree mounted yet.
    return {
      theme: undefined,
      resolvedTheme: undefined,
      setTheme: () => {},
    }
  }
  return ctx
}
